// src/communaute/PostCard.jsx
//
// Carte d'une publication (même ordre que l'app) :
// en-tête · titre · description · images · liens · actions

import { useEffect, useState } from 'react';
import {
  Bookmark,
  Check,
  ChevronLeft,
  ChevronRight,
  Facebook,
  Flag,
  Heart,
  Instagram,
  MessageCircle,
  MoreVertical,
  Music2,
  Plus,
  Trash2,
  X,
  Youtube,
} from 'lucide-react';

import { monId, signalerPost, supprimerPost, toggleFavori, toggleFollow, toggleLike } from './api';
import CommentsModal from './CommentsModal';
import { tempsRelatif, texteBrut } from './normalize';
import { Avatar, Modal, confirmer, toast } from './ui';

const MOTIFS = ['Contenu inapproprié', 'Spam ou publicité', 'Fausse information', 'Harcèlement', 'Autre'];

export default function PostCard({ post: initial, onSupprime, ouvrirCommentaires = false }) {
  const [post, setPost] = useState(initial);
  const [menu, setMenu] = useState(false);
  const [signalement, setSignalement] = useState(false);
  const [commentaires, setCommentaires] = useState(ouvrirCommentaires);
  const [deplie, setDeplie] = useState(false);
  const [index, setIndex] = useState(0);
  const [plein, setPlein] = useState(null); // index image plein écran
  const [busy, setBusy] = useState({});

  useEffect(() => setPost(initial), [initial]);

  const moi = monId();
  const estMoi = post.isMine || (moi > 0 && post.auteur.id === moi);
  const texte = texteBrut(post.contenu);
  const long = texte.length > 280 || texte.split('\n').length > 4;
  const imgs = post.images;

  // ── Like (optimiste) ──
  const like = async () => {
    if (busy.like) return;
    setBusy((b) => ({ ...b, like: true }));
    const avant = post;
    setPost((p) => ({ ...p, isLiked: !p.isLiked, nbrLikes: p.nbrLikes + (p.isLiked ? -1 : 1) }));
    const r = await toggleLike(post.id);
    if (r) setPost((p) => ({ ...p, ...r }));
    else {
      setPost(avant);
      toast('Action impossible. Réessayez plus tard.');
    }
    setBusy((b) => ({ ...b, like: false }));
  };

  // ── Favori ──
  const favori = async () => {
    const avant = post.isFavory;
    setPost((p) => ({ ...p, isFavory: !avant }));
    const r = await toggleFavori(post.id);
    if (r === null) {
      setPost((p) => ({ ...p, isFavory: avant }));
      toast('Action impossible. Réessayez plus tard.');
    } else setPost((p) => ({ ...p, isFavory: r }));
  };

  // ── S'abonner à l'auteur ──
  const suivre = async () => {
    if (busy.follow) return;
    setBusy((b) => ({ ...b, follow: true }));
    const avant = post.auteur.isFollowed;
    setPost((p) => ({ ...p, auteur: { ...p.auteur, isFollowed: !avant } }));
    const r = await toggleFollow(post.auteur.id);
    if (r === null) {
      setPost((p) => ({ ...p, auteur: { ...p.auteur, isFollowed: avant } }));
      toast('Action impossible. Réessayez plus tard.');
    } else {
      setPost((p) => ({ ...p, auteur: { ...p.auteur, isFollowed: r } }));
      toast(r ? 'Abonnement ajouté' : 'Abonnement retiré', r);
    }
    setBusy((b) => ({ ...b, follow: false }));
  };

  const supprimer = async () => {
    setMenu(false);
    const ok = await confirmer({
      titre: 'Supprimer la publication ?',
      message: 'Elle sera définitivement supprimée, avec ses commentaires.',
      bouton: 'Supprimer',
    });
    if (!ok) return;
    if (await supprimerPost(post.id)) {
      toast('Publication supprimée', true);
      onSupprime?.(post.id);
    } else toast('Échec de la suppression');
  };

  const signaler = async (motif) => {
    setSignalement(false);
    const ok = await signalerPost(post.id, motif);
    toast(ok ? 'Signalement envoyé. Merci !' : 'Échec du signalement. Réessayez plus tard.', ok);
  };

  const liens = [
    post.liens.youtube && { label: 'YouTube', url: post.liens.youtube, icone: Youtube, couleur: '#FF0000' },
    post.liens.facebook && { label: 'Facebook', url: post.liens.facebook, icone: Facebook, couleur: '#1877F2' },
    post.liens.tiktok && { label: 'TikTok', url: post.liens.tiktok, icone: Music2, couleur: '#69C9D0' },
    post.liens.instagram && { label: 'Instagram', url: post.liens.instagram, icone: Instagram, couleur: '#E1306C' },
  ].filter(Boolean);

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* ══ EN-TÊTE ══ */}
      <div className="flex items-center gap-3 px-4 pb-2 pt-4">
        <div className="relative">
          <Avatar src={post.auteur.photo} taille={44} />
          {!estMoi && (
            <button
              onClick={suivre}
              title={post.auteur.isFollowed ? 'Se désabonner' : "S'abonner"}
              className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white text-white ${post.auteur.isFollowed ? 'bg-[#258561]' : 'bg-[#7C3AED]'
                }`}
            >
              {post.auteur.isFollowed ? <Check size={11} strokeWidth={3} /> : <Plus size={11} strokeWidth={3} />}
            </button>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-bold text-gray-900">{post.auteur.nom}</p>
          <p className="text-xs text-gray-500">{post.dateTexte || tempsRelatif(post.date)}</p>
        </div>

        <div className="relative">
          <button onClick={() => setMenu((m) => !m)} className="rounded-full p-2 text-gray-600 hover:bg-gray-100">
            <MoreVertical size={20} />
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
                {estMoi ? (
                  <button
                    onClick={supprimer}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-gray-100"
                  >
                    <Trash2 size={17} /> Supprimer la publication
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setMenu(false);
                      setSignalement(true);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-900 hover:bg-gray-100"
                  >
                    <Flag size={17} className="text-orange-600" /> Signaler
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ══ TITRE + DESCRIPTION ══ */}
      <div className="px-4 pb-3">
        <h3 className="text-[17px] font-bold leading-snug text-gray-900">{post.titre}</h3>
        {texte && (
          <>
            <p
              className={`mt-1.5 whitespace-pre-line break-words text-sm leading-relaxed text-gray-700 ${long && !deplie ? 'line-clamp-4' : ''
                }`}
            >
              {texte}
            </p>
            {long && (
              <button
                onClick={() => setDeplie((d) => !d)}
                className="mt-1 text-sm font-semibold text-[#9B59B6] hover:text-[#b57ad0]"
              >
                {deplie ? 'Voir moins' : 'Voir plus'}
              </button>
            )}
          </>
        )}
      </div>

      {/* ══ IMAGES ══ */}
      {imgs.length === 1 && (
        <div className="px-4">
          <img
            src={imgs[0]}
            alt=""
            onClick={() => setPlein(0)}
            className="max-h-[70vh] w-full cursor-zoom-in rounded-xl bg-white object-contain"
          />
        </div>
      )}

      {imgs.length > 1 && (
        <div className="px-4">
          <div className="relative overflow-hidden rounded-xl bg-white">
            <img
              src={imgs[index]}
              alt=""
              onClick={() => setPlein(index)}
              className="h-[460px] w-full cursor-zoom-in object-contain"
            />
            {index > 0 && (
              <button
                onClick={() => setIndex((i) => i - 1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            {index < imgs.length - 1 && (
              <button
                onClick={() => setIndex((i) => i + 1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
              >
                <ChevronRight size={20} />
              </button>
            )}
            <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
              {index + 1}/{imgs.length}
            </span>
          </div>
          <div className="mt-2 flex justify-center gap-1.5">
            {imgs.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`rounded-full transition-all ${i === index ? 'h-2 w-2 bg-[#9B59B6]' : 'h-1.5 w-1.5 bg-gray-300'
                  }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* ══ LIENS ══ */}
      {liens.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {liens.map(({ label, url, icone: Icone, couleur }) => (
            <a
              key={label}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold"
              style={{ color: couleur, borderColor: `${couleur}66`, backgroundColor: `${couleur}26` }}
            >
              <Icone size={15} /> {label}
            </a>
          ))}
        </div>
      )}

      {/* ══ ACTIONS ══ */}
      <div className="flex items-center gap-1 px-2 py-2">
        <button
          onClick={like}
          className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-sm hover:bg-gray-100 ${post.isLiked ? 'text-red-600' : 'text-gray-600'
            }`}
        >
          <Heart size={21} fill={post.isLiked ? 'currentColor' : 'none'} /> {post.nbrLikes}
        </button>
        <button
          onClick={() => setCommentaires(true)}
          className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
        >
          <MessageCircle size={21} /> {post.nbrComments}
        </button>
        <button
          onClick={favori}
          title="Enregistrer"
          className={`ml-auto rounded-full p-2 hover:bg-gray-100 ${post.isFavory ? 'text-[#9B59B6]' : 'text-gray-600'}`}
        >
          <Bookmark size={21} fill={post.isFavory ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* ══ MODALES ══ */}
      {commentaires && (
        <CommentsModal
          post={post}
          ouvert={commentaires}
          onFermer={() => setCommentaires(false)}
          onCompte={(n) => setPost((p) => ({ ...p, nbrComments: n }))}
        />
      )}

      <Modal ouvert={signalement} onFermer={() => setSignalement(false)} titre="Signaler cette publication" taille="sm">
        <div className="py-2">
          {MOTIFS.map((m) => (
            <button
              key={m}
              onClick={() => signaler(m)}
              className="block w-full px-5 py-3 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              {m}
            </button>
          ))}
        </div>
      </Modal>

      {plein !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95" onClick={() => setPlein(null)}>
          <button className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20">
            <X size={22} />
          </button>
          {plein > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPlein((p) => p - 1);
              }}
              className="absolute left-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <ChevronLeft size={26} />
            </button>
          )}
          <img
            src={imgs[plein]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] max-w-[92vw] object-contain"
          />
          {plein < imgs.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPlein((p) => p + 1);
              }}
              className="absolute right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <ChevronRight size={26} />
            </button>
          )}
        </div>
      )}
    </article>
  );
}